import React, { useEffect, useState } from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td, Button, Flex, Text, Textarea } from '@chakra-ui/react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import LoadingOverlayText from '../LoadingOverlay/LoadingOverlayText';
import { useTranslation } from 'react-i18next';
import useStore from '../../store';
import c from '../../constant';

const GET_TIMESHEET_DATA = gql`
	query getEmployeeTimeSheet($month: String!, $uid: String!) {
		getEmployeesTimeSheetByUid(uid: $uid, month: $month) {
			id
			uid
			queryDate
			date
		}
	}
`;

/**
 * Days header
 */
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_KH = ['អាទិត្យ', 'ចន្ទ', 'អង្គារ', 'ពុធ', 'ព្រហស្បតិ៍', 'សុក្រ', 'សៅរ៍'];

/**
 * Build month matrix (Sunday → Saturday)
 */
function getMonthMatrix(year, month) {
	const firstDay = new Date(year, month, 1);
	const lastDay = new Date(year, month + 1, 0);

	const matrix = [];
	let week = new Array(7).fill(null);
	let dayIndex = firstDay.getDay();

	for (let date = 1; date <= lastDay.getDate(); date++) {
		week[dayIndex] = new Date(year, month, date);
		if (dayIndex === 6 || date === lastDay.getDate()) {
			matrix.push(week);
			week = new Array(7).fill(null);
		}
		dayIndex = (dayIndex + 1) % 7;
	}
	return matrix;
}

/**
 * Format date as YYYY-MM-DD
 */
function formatDateKey(date) {
	return date.toLocaleDateString('en-CA', {
		timeZone: 'Asia/Phnom_Penh',
	});
}


export default function MonthlyTimesheet({ uid }) {
	const [currentDate, setCurrentDate] = useState(new Date());
	const [notes, setNotes] = useState({}); // { "YYYY-MM-DD": "note" }
	const { t } = useTranslation();

	const { currentLang } = useStore((state) => state.currentLang);

	const [getTimeSheetData, { error, data, loading }] = useLazyQuery(GET_TIMESHEET_DATA);
	useEffect(() => {
		if (uid) {
			const yyyyMm = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0');
			getTimeSheetData({
				variables: {
					uid,
					month: yyyyMm,
				},
			});
		}
	}, [uid, currentDate]);

	const [lastQuery, setLastQuery] = useState(null);
	useEffect(() => {
		if (data?.getEmployeesTimeSheetByUid) {
			const tmpData = convertTimesheetData(data?.getEmployeesTimeSheetByUid);
			console.log("tmpData;", tmpData)
			setNotes(tmpData);
			getLatestQueryDate(data?.getEmployeesTimeSheetByUid);
			if (data?.getEmployeesTimeSheetByUid?.length > 0) {
				setLastQuery(getLatestQueryDate(data?.getEmployeesTimeSheetByUid));
			}
		}
	}, [data]);

	function convertTimesheetData(data) {
		const result = {};

		data.forEach((item) => {
			const dateObj = new Date(item.date);

			// LOCAL YYYY-MM-DD (Cambodia time)
			const dateKey = dateObj.toLocaleDateString('en-CA', {
				timeZone: 'Asia/Phnom_Penh',
			}); // en-CA gives YYYY-MM-DD format

			// format time -> 08:05am (Cambodia time)
			const time = dateObj
				.toLocaleTimeString('en-US', {
					timeZone: 'Asia/Phnom_Penh',
					hour: '2-digit',
					minute: '2-digit',
					hour12: true,
				})
				.toLowerCase();

			if (!result[dateKey]) {
				result[dateKey] = [];
			}

			result[dateKey].push(time);
		});

		// convert array → multiline string
		Object.keys(result).forEach((key) => {
			result[key] = result[key].join('\n');
		});

		return result;
	}


	const year = currentDate.getFullYear();
	const month = currentDate.getMonth();
	const matrix = getMonthMatrix(year, month);

	const changeMonth = (offset) => {
		setCurrentDate(new Date(year, month + offset, 1));
	};

	const isToday = (date) => date.toDateString() === new Date().toDateString();

	function getLatestQueryDate(data) {
		if (!data || data.length === 0) return null;

		// Map all queryDate to timestamps and get max
		const latest = data.reduce((max, item) => {
			const time = new Date(item.queryDate).getTime();
			return time > max ? time : max;
		}, 0);

		return new Date(latest);
	}

	return (
		<Box w="100%" p={4}>
			{loading && <LoadingOverlayText />}
			{error && <Text color="red">We're sorry, but something went wrong!</Text>}
			{/* Header */}
			<Flex justify="space-between" align="center" mb={4}>
				<Button size="sm" onClick={() => changeMonth(-1)}>
					◀ {t('Prev')}
				</Button>

				<Text fontSize="xl" fontWeight="bold">
					{currentDate.toLocaleDateString('en-US', {
						month: 'numeric',
						year: 'numeric',
					})}
				</Text>

				<Button size="sm" onClick={() => changeMonth(1)}>
					{t('Next')} ▶
				</Button>
			</Flex>

			{/* Table */}
			<Box overflowX="auto">
				<Table variant="simple" size="sm" w="100%" tableLayout="fixed">
					<Thead bg="gray.100">
						<Tr>
							{currentLang === 'kh' ? (
								<>
									{DAYS_KH.map((day) => (
										<Th key={day} textAlign="center" border="1px solid #bdc3c7">
											{t(day)}
										</Th>
									))}
								</>
							) : (
								<>
									{DAYS.map((day) => (
										<Th key={day} textAlign="center" border="1px solid #bdc3c7">
											{t(day)}
										</Th>
									))}
								</>
							)}
						</Tr>
					</Thead>

					<Tbody>
						{matrix.map((week, wi) => (
							<Tr key={wi}>
								{week.map((date, di) => (
									<Td
										key={di}
										border="1px solid #bdc3c7"
										_hover={{ bg: 'rgba(0,0,0,.1)' }}
										h="140px"
										verticalAlign="top"
										p={2}
										bg={date ? (isToday(date) ? 'blue.50' : 'white') : 'gray.50'}
									>
										{date && (
											<>
												<Text fontWeight="bold" fontSize="sm" mb={1}>
													{date.getDate()}
												</Text>
												<Box color="rgba(46, 204, 113,1.0)" fontSize="16px" fontWeight="500">
													<pre style={{ textAlign: 'right' }}>{notes[formatDateKey(date)] || ''}</pre>
												</Box>
											</>
										)}
									</Td>
								))}
							</Tr>
						))}
					</Tbody>
				</Table>
			</Box>
			<Text>
				{t('Last Update')}: {data?.getEmployeesTimeSheetByUid && (lastQuery ? moment(lastQuery).format(c.excelDateTimeFormat) : '')}
			</Text>
		</Box>
	);
}
